/* ==========================================================================
   SSG SKU — attach product SKU as a public line-item property at add-to-cart
   so the SKU is visible in the cart, drawer, checkout, and order confirmation
   email. Empire's native SKU block handles display on PDP/quickshop/cart page
   themselves; this script ensures checkout-time visibility (where the theme
   has no Liquid hook) by piggybacking on Shopify's line-item properties.

   How it works:
   1. Intercept form submits on any form posting to /cart/add (Empire AJAX +
      classic POST). For the chosen variant, look up its SKU and inject a
      hidden <input name="properties[SKU]"> if missing.
   2. Monkey-patch window.fetch so any direct fetch('/cart/add.js', ...) call
      also gets the SKU attached. Covers third-party "buy now" buttons,
      quick-add cards, and Shopify Buy SDK widgets that use fetch().

   We use a property called "SKU" (no leading underscore), so it's visible to
   the customer in cart & checkout. Use a different name if you'd rather keep
   it internal-only (prefix with "_" to hide from customer-facing UI).
   ========================================================================== */

(function () {
  'use strict';

  /* ----- Cache: product handle → variants (id → sku) ----- */
  var variantCache = Object.create(null);
  var inFlight = Object.create(null);

  function getProductVariants(handle) {
    if (!handle) return Promise.resolve(null);
    if (variantCache[handle]) return Promise.resolve(variantCache[handle]);
    if (inFlight[handle]) return inFlight[handle];
    inFlight[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.variants) return null;
        var map = {};
        data.variants.forEach(function (v) { map[String(v.id)] = v.sku || ''; });
        variantCache[handle] = map;
        return map;
      })
      .catch(function () { return null; })
      .then(function (m) { delete inFlight[handle]; return m; });
    return inFlight[handle];
  }

  function getVariantSkuFromForm(form) {
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return Promise.resolve(null);
    var variantId = String(idInput.value || '').trim();
    if (!variantId) return Promise.resolve(null);

    // Try product handle via data-product-handle or data-product-json
    var handleHost = form.closest('[data-product-handle]') ||
                     document.querySelector('[data-product-handle]');
    var handle = handleHost ? handleHost.getAttribute('data-product-handle') : null;

    // Fallback: read product JSON if embedded
    if (!handle) {
      var jsonEl = document.querySelector('script[type="application/json"][data-product-json], #ProductJson-product-template');
      if (jsonEl) {
        try {
          var p = JSON.parse(jsonEl.textContent);
          if (p && p.variants) {
            var hit = p.variants.find(function (v) { return String(v.id) === variantId; });
            if (hit && hit.sku) return Promise.resolve(hit.sku);
          }
        } catch (e) { /* ignore */ }
      }
      // Last resort: parse URL for /products/<handle>
      var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      if (m) handle = m[1];
    }
    if (!handle) return Promise.resolve(null);
    return getProductVariants(handle).then(function (map) {
      return map ? (map[variantId] || null) : null;
    });
  }

  function ensureSkuInput(form, sku) {
    if (!sku) return;
    var existing = form.querySelector('input[name="properties[SKU]"]');
    if (existing) {
      existing.value = sku;
    } else {
      var inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = 'properties[SKU]';
      inp.value = sku;
      form.appendChild(inp);
    }
  }

  /* ----- 1. Intercept form submits to /cart/add ----- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    if (form.querySelector('input[name="properties[SKU]"]')) return;

    // Pause submission, look up SKU, then resubmit. Form submit can be sync;
    // we use preventDefault + a flag so we don't loop.
    if (form.dataset.ssgSkuChecked === '1') return;
    e.preventDefault();
    form.dataset.ssgSkuChecked = '1';
    getVariantSkuFromForm(form).then(function (sku) {
      ensureSkuInput(form, sku);
      // Re-fire the original submit event — Empire's AJAX handler will catch it,
      // or the form will POST natively if no JS handler intercepts.
      try { form.requestSubmit(); }
      catch (err) { form.submit(); }
    });
  }, true);

  /* ----- 2. Wrap fetch() so direct /cart/add.js calls get the SKU too ----- */
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') === -1) return origFetch.apply(this, arguments);
        if (!init || !init.body) return origFetch.apply(this, arguments);

        var contentType = (init.headers && (init.headers['Content-Type'] || init.headers['content-type'])) || '';
        var body = init.body;

        // JSON body case
        if (body && typeof body === 'string' && contentType.indexOf('json') !== -1) {
          try {
            var parsed = JSON.parse(body);
            // Two shapes: { id, quantity, properties? } or { items: [{ id, quantity, properties? }, ...] }
            return injectSkuIntoPayload(parsed).then(function (p) {
              init.body = JSON.stringify(p);
              return origFetch.call(window, input, init);
            });
          } catch (e) { /* fall through */ }
        }

        // FormData body case
        if (body instanceof FormData) {
          if (body.has('properties[SKU]')) return origFetch.apply(this, arguments);
          var idVal = body.get('id');
          if (!idVal) return origFetch.apply(this, arguments);
          return resolveSkuForVariant(String(idVal)).then(function (sku) {
            if (sku) body.set('properties[SKU]', sku);
            return origFetch.call(window, input, init);
          });
        }

        // URLSearchParams / string form-encoded
        if (body && typeof body === 'string' && contentType.indexOf('form-urlencoded') !== -1) {
          var params = new URLSearchParams(body);
          if (params.has('properties[SKU]')) return origFetch.apply(this, arguments);
          var pid = params.get('id');
          if (!pid) return origFetch.apply(this, arguments);
          return resolveSkuForVariant(String(pid)).then(function (sku) {
            if (sku) params.set('properties[SKU]', sku);
            init.body = params.toString();
            return origFetch.call(window, input, init);
          });
        }
      } catch (e) { /* swallow and call through */ }
      return origFetch.apply(this, arguments);
    };
  }

  function injectSkuIntoPayload(p) {
    if (!p) return Promise.resolve(p);
    if (p.items && Array.isArray(p.items)) {
      // Multi-item add (e.g., bundles)
      return Promise.all(p.items.map(function (it) {
        if (!it || !it.id) return it;
        if (it.properties && it.properties.SKU) return it;
        return resolveSkuForVariant(String(it.id)).then(function (sku) {
          if (sku) {
            it.properties = it.properties || {};
            it.properties.SKU = sku;
          }
          return it;
        });
      })).then(function () { return p; });
    }
    if (p.id) {
      if (p.properties && p.properties.SKU) return Promise.resolve(p);
      return resolveSkuForVariant(String(p.id)).then(function (sku) {
        if (sku) {
          p.properties = p.properties || {};
          p.properties.SKU = sku;
        }
        return p;
      });
    }
    return Promise.resolve(p);
  }

  function resolveSkuForVariant(variantId) {
    // First try DOM-embedded product JSON for this page
    var jsonEl = document.querySelector('script[type="application/json"][data-product-json]');
    if (jsonEl) {
      try {
        var p = JSON.parse(jsonEl.textContent);
        if (p && p.variants) {
          var hit = p.variants.find(function (v) { return String(v.id) === variantId; });
          if (hit && hit.sku) return Promise.resolve(hit.sku);
        }
      } catch (e) { /* ignore */ }
    }
    // Otherwise, scan the cached variant maps we already fetched
    for (var handle in variantCache) {
      if (variantCache[handle] && variantCache[handle][variantId]) {
        return Promise.resolve(variantCache[handle][variantId]);
      }
    }
    // Last resort: page URL handle
    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (m) {
      return getProductVariants(m[1]).then(function (map) {
        return map ? (map[variantId] || null) : null;
      });
    }
    return Promise.resolve(null);
  }
})();
